import type { MidiClip, Project } from '@/domain/types';
import { isMidiClip } from '@/domain/types';
import { isTrackAudible, tempoMapOf } from '@/domain/selectors';
import { playsFrozen, silencedByFrozenBus } from '@/domain/freeze';
import { ticksToSeconds } from '@/timing/tempoMap';
import type { PlanWindow } from './schedulePlan';

/**
 * MIDI scheduling, as pure data.
 *
 * The same separation the audio scheduler uses: deciding *which notes sound
 * when* is arithmetic over the project, and is tested without a browser; making
 * them sound is a mechanical step in the engine.
 *
 * A note is planned exactly once, when its start falls inside the window being
 * filled. Notes that began before the playhead are **not** retriggered — a note
 * restarted from its middle at the wrong point in its envelope sounds like a
 * mistake, and skipping them matches what every DAW does by default.
 */

export interface NotePlan {
  trackId: string;
  clipId: string;
  noteId: string;
  pitch: number;
  velocity: number;
  /** Project position, in seconds, at which the note starts. */
  startProjectSec: number;
  /** Sounding length in seconds, already truncated at the segment end. */
  durationSec: number;
}

/** Highest number of loop repetitions considered for one clip, as a safety bound. */
const MAX_REPEATS = 4096;

export function planMidiWindow(project: Project, window: PlanWindow): NotePlan[] {
  const map = tempoMapOf(project);
  const plans: NotePlan[] = [];

  for (const trackId of project.trackOrder) {
    const track = project.tracks[trackId];
    if (!track || track.kind !== 'instrument' || !track.instrument) continue;
    if (!isTrackAudible(project, trackId)) continue;
    // A frozen track's notes are already in its render; playing them again
    // would double the part.
    if (playsFrozen(project, track)) continue;
    if (silencedByFrozenBus(project, track.id)) continue;

    for (const clipId of track.clipIds) {
      const clip = project.clips[clipId];
      if (!clip || clip.muted || !isMidiClip(clip)) continue;
      // Alternate takes are kept for comping and never scheduled.
      if (clip.takeLane !== 0) continue;
      collectClip(map, clip, window, plans);
    }
  }

  return plans;
}

function collectClip(
  map: ReturnType<typeof tempoMapOf>,
  clip: MidiClip,
  window: PlanWindow,
  out: NotePlan[],
): void {
  const clipEndTick = clip.startTicks + clip.durationTicks;
  const loopLength = clip.loop ? Math.max(1, clip.loopLengthTicks) : clip.durationTicks;
  const repeats = clip.loop ? Math.min(MAX_REPEATS, Math.ceil(clip.durationTicks / loopLength)) : 1;

  for (const note of clip.notes) {
    for (let repeat = 0; repeat < repeats; repeat++) {
      // A looped clip repeats its notes every `loopLengthTicks` until the clip
      // ends; notes past the loop length in the first pass are simply not heard.
      if (clip.loop && note.startTicks >= loopLength) break;

      const startTick = clip.startTicks + repeat * loopLength + note.startTicks;
      if (startTick >= clipEndTick) break;

      const startSec = ticksToSeconds(map, startTick);
      if (startSec < window.segmentStartSec) continue;
      if (startSec < window.windowStartSec || startSec >= window.windowEndSec) continue;

      // A note is cut short by the end of its clip, and by the end of the
      // segment — a loop boundary must not leave a note ringing into the wrap.
      const endTick = Math.min(startTick + note.durationTicks, clipEndTick);
      const endSec = Math.min(ticksToSeconds(map, endTick), window.segmentEndSec);
      const durationSec = endSec - startSec;
      if (durationSec <= 0) continue;

      out.push({
        trackId: clip.trackId,
        clipId: clip.id,
        noteId: note.id,
        pitch: note.pitch,
        velocity: note.velocity,
        startProjectSec: startSec,
        durationSec,
      });
    }
  }
}

/** Furthest tick reached by any note in a MIDI clip, for sizing the editor. */
export function midiClipContentEndTicks(clip: MidiClip): number {
  let end = 0;
  for (const note of clip.notes) end = Math.max(end, note.startTicks + note.durationTicks);
  return end;
}

/** Lowest and highest pitches used by a clip, for framing the piano roll. */
export function midiClipPitchRange(clip: MidiClip): { low: number; high: number } {
  if (clip.notes.length === 0) return { low: 48, high: 72 };
  let low = 127;
  let high = 0;
  for (const note of clip.notes) {
    if (note.pitch < low) low = note.pitch;
    if (note.pitch > high) high = note.pitch;
  }
  return { low, high };
}
